import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { mapLegacyRole } from '@/utils/roles'

const PendingLinksCountContext = createContext({
  pendingCount: 0,
  isLoading: false,
  error: null,
  refetch: () => Promise.resolve(),
  lastUpdated: null
})

export const PendingLinksCountProvider = ({ children }) => {
  const location = useLocation()
  const { user, professionalProfile } = useAuth()
  const normalizedRole = useMemo(
    () => mapLegacyRole(professionalProfile?.role),
    [professionalProfile?.role]
  )

  const [pendingCount, setPendingCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const debounceRef = useRef(null)
  const inFlightRef = useRef(null)
  const isMountedRef = useRef(true)

  const canViewPending = Boolean(user?.id && (normalizedRole === 'admin' || normalizedRole === 'emd'))

  const fetchPendingCount = useCallback(async () => {
    if (!canViewPending) {
      setPendingCount(0)
      setLastUpdated(null)
      setError(null)
      setIsLoading(false)
      return
    }

    if (inFlightRef.current) {
      return inFlightRef.current
    }

    setIsLoading(true)
    setError(null)

    const request = (async () => {
      try {
        const { count: total, error: queryError } = await supabase
          .from('agent_user_links_pending')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending')

        if (queryError) {
          throw queryError
        }

        if (!isMountedRef.current) return

        setPendingCount(total ?? 0)
        setLastUpdated(new Date())
      } catch (err) {
        if (!isMountedRef.current) return
        console.error('Failed to fetch pending links count:', err)
        setPendingCount(0)
        setError(err)
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false)
        }
        inFlightRef.current = null
      }
    })()

    inFlightRef.current = request
    return request
  }, [canViewPending])

  const refetch = useCallback(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchPendingCount()
    }, 300)

    return inFlightRef.current ?? Promise.resolve()
  }, [fetchPendingCount])

  useEffect(() => {
    fetchPendingCount()
  }, [fetchPendingCount])

  useEffect(() => {
    if (location.pathname?.startsWith('/agent-tracker')) {
      refetch()
    }
  }, [location.pathname, refetch])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refetch()
      }
    }

    window.addEventListener('focus', handleVisibility)
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      window.removeEventListener('focus', handleVisibility)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [refetch])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      clearTimeout(debounceRef.current)
    }
  }, [])

  const value = useMemo(
    () => ({ pendingCount, isLoading, error, refetch, lastUpdated }),
    [pendingCount, isLoading, error, refetch, lastUpdated]
  )

  return (
    <PendingLinksCountContext.Provider value={value}>
      {children}
    </PendingLinksCountContext.Provider>
  )
}

export const usePendingLinksCount = () => useContext(PendingLinksCountContext)
